import {
  createApi,
  fetchBaseQuery,
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import type { ThemeVariables } from "../types";

// --- Constants ---
const CART_TOKEN_STORAGE_KEY = "wc_cart_token";

// --- Token Management ---

// Attempt to load Cart-Token from localStorage on initial load
let currentCartToken: string | null = localStorage.getItem(
  CART_TOKEN_STORAGE_KEY
);

// This nonce is for authenticating with the WP REST API (for our custom endpoints)
let currentWpRestApiNonce: string | undefined = (
  window.themeVariables as ThemeVariables | undefined
)?.wpRestApiNonce;

const saveCartTokenToStorage = (token: string | null) => {
  if (token) {
    localStorage.setItem(CART_TOKEN_STORAGE_KEY, token);
  } else {
    localStorage.removeItem(CART_TOKEN_STORAGE_KEY);
  }
};

const updateCartToken = (newToken: string | null) => {
  if (newToken !== currentCartToken) {
    currentCartToken = newToken;
    saveCartTokenToStorage(currentCartToken);
  }
};

const updateWpRestNonce = (newNonce: string | undefined) => {
  if (newNonce && newNonce !== currentWpRestApiNonce) {
    currentWpRestApiNonce = newNonce;
  }
};

const rawBaseQuery = fetchBaseQuery({
  baseUrl: (window.themeVariables as ThemeVariables | undefined)?.siteUrl || "",
  prepareHeaders: (headers) => {
    // Correctly set both headers if they exist ---

    // 1. Add the Cart-Token header if it exists. This is used by the WC Store API.
    if (currentCartToken) {
      headers.set("Cart-Token", currentCartToken);
    }

    // 2. Add the WP REST API Nonce header if it exists. This is used for our custom endpoints.
    // This is NOT an "else if" because some pages might make calls to both APIs.
    if (currentWpRestApiNonce) {
      // Use the correct header name: "X-WP-Nonce"
      headers.set("X-WP-Nonce", currentWpRestApiNonce);
    }

    return headers;
  },
  credentials: "same-origin",
});

const customBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const result = await rawBaseQuery(args, api, extraOptions);

  if (result.meta?.response?.headers) {
    const headers = result.meta.response.headers;

    // Check for both tokens/nonces in the response ---
    // Update Cart-Token (for WC Store API)
    const newCartToken = headers.get("Cart-Token");
    if (newCartToken) {
      updateCartToken(newCartToken);
    }

    // Update WP REST API Nonce (for our custom endpoints and general WP REST)
    const newWpRestNonce = headers.get("X-WP-Nonce");
    if (newWpRestNonce) {
      updateWpRestNonce(newWpRestNonce);
    }
  }

  if (
    result.error &&
    typeof result.error === "object" &&
    "status" in result.error
  ) {
    if (result.error.status === 401 || result.error.status === 403) {
      console.warn(
        `[API] Received ${result.error.status} error. Tokens or nonce may be expired.`
      );
    }
  }
  return result;
};

export const baseApi = createApi({
  reducerPath: "api",
  baseQuery: customBaseQuery,
  tagTypes: [
    "Products",
    "Variations",
    "Categories",
    "Tags",
    "Attributes",
    "Brands",
    "Cart",
    "Checkout",
    "PaymentMethods",
    "Cities",
    "CitiesWithDistricts",
    "Order",
  ],
  endpoints: () => ({}),
});

export const {
  util: { getRunningQueriesThunk },
  reducer: apiReducer,
  middleware: apiMiddleware,
} = baseApi;

// Export the updated nonce management function if needed elsewhere
export { updateCartToken, updateWpRestNonce };
